// TodaysNote — the single home-screen card. One number + one
// contextual sentence + one primary CTA.
//
// Replaces the previous Zone A which stacked progress ring + streak +
// money cards. Those move into the "How today's going" expander below
// so the data is still one tap away.
//
// Contextual sentence priority:
//   1. Yesterday's pledge (morning only — "Yesterday's why: '…'")
//   2. Honest progress line ("X mg under target" / "X mg over · data not verdict")
//
// CTA priority is time-of-day:
//   night    (23:00–05:00) → "Breathe with me · 60s"  (handled in Phase 19)
//   morning  (05:00–11:59) → "Start the day"  (scrolls to Tools)
//   day      (12:00–17:59) → "Quick log"
//   evening  (18:00–22:59) → "Tonight's check-in"
//
// Phase 17 lands the morning+day+evening branches; the night branch
// links to the Phase 19 BreatheScreen route when that ships.

import type { ReactNode } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useTickUp } from '../../lib/motion';
import { fmtMg } from '../../lib/format';

export type TodayPeriod = 'morning' | 'day' | 'evening' | 'night';

interface TodaysNoteProps {
  actual: number;
  target: number;
  period: TodayPeriod;
  /** Yesterday's pledge, if any. Surfaced as the first line during morning. */
  yesterdaysPledge: string | null;
  /** Primary CTA — the parent decides the handler so we can navigate. */
  cta: { label: string; onClick: () => void } | null;
  /** Optional appendage below the headline (e.g., expanding details). */
  footer?: ReactNode;
}

export function TodaysNote({
  actual,
  target,
  period,
  yesterdaysPledge,
  cta,
  footer,
}: TodaysNoteProps) {
  const animatedActual = useTickUp(actual, { durationMs: 600, ease: 'emphasis' });

  // Pick the contextual sentence.
  let sentence: ReactNode;
  if (period === 'morning' && yesterdaysPledge) {
    sentence = (
      <p className="todays-note-pledge">
        <span className="todays-note-pledge-label">Yesterday's why</span>
        <span className="todays-note-pledge-text">{`"${yesterdaysPledge}"`}</span>
      </p>
    );
  } else if (target > 0 && actual > target) {
    sentence = (
      <p className="muted todays-note-sentence">
        {`${fmtMg(actual - target)} over today's target. Today is data, not a verdict.`}
      </p>
    );
  } else if (target > 0) {
    sentence = (
      <p className="muted todays-note-sentence">
        {`${fmtMg(Math.max(0, target - actual))} under today's target.`}
      </p>
    );
  } else {
    sentence = (
      <p className="muted todays-note-sentence">
        Welcome. Lock your baseline to see your taper plan.
      </p>
    );
  }

  // Phase 2 — 1-word status above the mg number. Three states:
  //   "Over today" (warning dot) when actual > target with a real target.
  //   "New day"    (success dot) when nothing's been logged yet.
  //   "On track"   (success dot) otherwise (at or under target, including
  //                target = 0 because there's no verdict to fail).
  // Caveat handled: if both `target` and `actual` end up 0 we fall into
  // the "New day" branch (success dot), which matches the spec.
  const statusOver = target > 0 && actual > target;
  const statusNew = actual === 0;
  const statusLabel: string = statusOver ? 'Over today' : statusNew ? 'New day' : 'On track';
  const statusTone: 'over' | 'ok' = statusOver ? 'over' : 'ok';

  return (
    <Card className="todays-note">
      <div className="todays-note-status" data-tone={statusTone}>
        <span className="todays-note-status-dot" aria-hidden="true" />
        <span className="todays-note-status-word">{statusLabel}</span>
      </div>
      <div className="todays-note-headline">
        <span className="todays-note-number">{animatedActual.toFixed(1)}</span>
        <span className="todays-note-target">{` / ${target.toFixed(1)} mg`}</span>
      </div>
      {sentence}
      {cta ? (
        <Button size="lg" block onClick={cta.onClick} className="todays-note-cta">
          {cta.label}
        </Button>
      ) : null}
      {footer}
    </Card>
  );
}
